"use client";

import { Link } from "react-router-dom";
import { useMe } from "@/hooks/api/useMe";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import {
  Megaphone,
  MessageCircle,
  Ticket,
  Tag,
  LayoutGrid,
  Sparkles,
  Target,
  Briefcase,
  TrendingUp,
} from "lucide-react";

const adminTiles = [
  {
    href: "/marketing/whatsapp",
    title: "WhatsApp",
    desc: "Bulk template send, delivery log via API.",
    icon: MessageCircle,
  },
  {
    href: "/marketing/vouchers",
    title: "Vouchers",
    desc: "Create codes, balances, redemptions.",
    icon: Ticket,
  },
  {
    href: "/marketing/offers",
    title: "Offers",
    desc: "Active promos and validity window.",
    icon: Tag,
  },
  {
    href: "/marketing/catalogue",
    title: "Catalogue",
    desc: "Pick products, order, preview message draft.",
    icon: LayoutGrid,
  },
];

const staffTiles = [
  {
    href: "/product-admin/leads",
    title: "My Leads",
    desc: "Add new prospects, manage status pipeline.",
    icon: Target,
  },
  {
    href: "/product-admin/dashboard",
    title: "Dashboard",
    desc: "Conversion stats and recent activity.",
    icon: TrendingUp,
  },
  {
    href: "/marketing/whatsapp",
    title: "Outreach",
    desc: "WhatsApp pitch + follow-ups to leads.",
    icon: MessageCircle,
  },
  {
    href: "/marketing/catalogue",
    title: "Pitch deck",
    desc: "Product catalogue to share with prospects.",
    icon: Briefcase,
  },
];

type LeadRow = {
  id: number;
  status: string;
};

export default function MarketingHubPage() {
  const { data: me } = useMe();
  const role = me?.role;
  const isStaff = role === "product_admin_staff";

  const { data: leads } = useQuery({
    queryKey: ["product-admin", "leads", "marketing-hub"],
    enabled: isStaff,
    queryFn: async () => {
      const { data } = await api.get<{ leads: LeadRow[] }>("/api/product-admin/leads");
      return data.leads;
    },
  });

  if (isStaff) {
    const total = leads?.length ?? 0;
    const converted = leads?.filter((l) => l.status === "converted").length ?? 0;
    const open = leads?.filter((l) => l.status !== "converted" && l.status !== "lost").length ?? 0;

    return (
      <div className="min-h-full space-y-6 bg-background p-6">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">Sales workspace</h1>
                <p className="text-sm text-muted-foreground">
                  Convert leads, run outreach, share product pitches.
                </p>
              </div>
            </div>
            <Link
              to="/product-admin/leads"
              className={buttonVariants({ variant: "default", size: "sm" })}
            >
              + Add lead
            </Link>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-muted/50 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">My leads</p>
              <p className="mt-1 text-2xl font-semibold text-foreground">{total}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/50 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Open pipeline</p>
              <p className="mt-1 text-2xl font-semibold text-foreground">{open}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/50 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Converted</p>
              <p className="mt-1 text-2xl font-semibold text-primary">{converted}</p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Quick actions
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {staffTiles.map((t) => {
              const Icon = t.icon;
              return (
                <Link key={t.href} to={t.href} className="group">
                  <Card className="h-full border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                    <CardHeader className="space-y-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base text-foreground">{t.title}</CardTitle>
                        <CardDescription className="mt-1">{t.desc}</CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {leads && leads.length > 0 ? (
          <Card className="border-border bg-card shadow-sm">
            <CardHeader className="border-b border-border bg-muted/50">
              <CardTitle className="text-base text-foreground">Pipeline snapshot</CardTitle>
              <CardDescription>Status breakdown of your leads.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-2">
                {(["new", "contacted", "qualified", "converted", "lost"] as const).map((s) => {
                  const count = leads.filter((l) => l.status === s).length;

                  return (
                    <div
                      key={s}
                      className="rounded-full border border-border bg-muted/60 px-3 py-1 text-xs"
                    >
                      <span className="text-muted-foreground">{s}</span>
                      <span className="ml-2 font-semibold text-primary">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-6 bg-background p-6">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Megaphone className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Marketing</h1>
            <p className="text-sm text-muted-foreground">
              CRM campaigns, vouchers, and offers.
            </p>
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {adminTiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.href} to={t.href}>
              <Card className="h-full border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                <CardHeader>
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-base text-foreground">{t.title}</CardTitle>
                  <CardDescription>{t.desc}</CardDescription>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
